import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { PageHeader, TestimonialCard, Loader } from "@/components/shared";
import { Star } from "lucide-react";

const FILTERS = ["All", "Individual", "Online", "Workshop"];

export default function Testimonials() {
  useSeo({
    title: "Client Stories & Testimonials — Family Constellation Work",
    description: "Real, consented stories from people who have done Family Constellation work with Arjunna Soni in Ludhiana and online. Read their experiences.",
    path: "/testimonials",
  });
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", serviceType: "Individual", quote: "", rating: 5, anonymise: true, consent: false });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  const shown = !items ? [] : filter === "All" ? items : items.filter((t) => t.serviceType === filter);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please give consent to share your story."); return; }
    setBusy(true);
    try {
      const res = await api.post("/testimonials/submit", form);
      toast.success(res.data.message);
      setOpen(false);
      setForm({ name: "", city: "", serviceType: "Individual", quote: "", rating: 5, anonymise: true, consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader crumb="Client Stories" title="Client stories" intro="Real words from people who've done this work. Shared with their consent, and often anonymised." />
      <section className="container-page pb-16 md:pb-24">
        <h2 className="text-2xl md:text-3xl text-earth mb-8" style={{ fontWeight: 600 }}>What clients say about the work</h2>
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${filter === f ? "bg-gold text-earth" : "bg-surface-card border hairline text-earth/70 hover:border-gold"}`}>
                {f}
              </button>
            ))}
          </div>
          <button onClick={() => setOpen(!open)} className="btn-outline text-sm" data-testid="share-story-btn">Share your story</button>
        </div>

        {open && (
          <form onSubmit={submit} className="bg-surface-card border hairline rounded-lg p-7 mb-10 grid sm:grid-cols-2 gap-4 animate-fade-in" data-testid="testimonial-form">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ts-name" />
            <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ts-city" />
            <select value={form.serviceType} onChange={(e) => setForm({ ...form, serviceType: e.target.value })} className="px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ts-service">
              <option>Individual</option><option>Online</option><option>Workshop</option>
            </select>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })}><Star size={22} className={n <= form.rating ? "fill-gold text-gold" : "text-earth/30"} /></button>
              ))}
            </div>
            <textarea required placeholder="Share what the work meant to you…" value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} rows={3} className="sm:col-span-2 px-4 py-3 rounded-md bg-parchment border hairline outline-none focus:border-gold" data-testid="ts-quote" />
            <label className="flex items-center gap-2 text-sm text-earth/70"><input type="checkbox" checked={form.anonymise} onChange={(e) => setForm({ ...form, anonymise: e.target.checked })} className="accent-[#C9A84C]" data-testid="ts-anon" /> Show my initials only (recommended)</label>
            <label className="flex items-center gap-2 text-sm text-earth/70"><input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="accent-[#C9A84C]" data-testid="ts-consent" /> I consent to my story being shared publicly.</label>
            <button type="submit" disabled={busy} className="btn-gold sm:col-span-2" data-testid="ts-submit">{busy ? "…" : "Submit for review"}</button>
          </form>
        )}

        {!items ? <Loader /> : shown.length === 0 ? (
          <p className="text-earth/60 py-16 text-center">No stories in this category yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {shown.map((t) => <TestimonialCard key={t.testimonial_id} t={t} />)}
          </div>
        )}

        <div className="mt-16 text-center">
          <Link to="/book" className="btn-gold">Begin your own story</Link>
        </div>
      </section>
    </div>
  );
}
